
import plansAPI from '~/api/plans';

export default {
  name: 'ClientPlanSelect',

  props: {
    value: {
      type: String | Array | Object | null,
      required: true,
    },
    label: {
      type: String,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    currentPlanUuid: {
      type: String,
      required: false,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    preserveModelType: {
      type: Boolean,
      required: false,
    },
    exclude: {
      type: Array,
      required: false,
      default: () => [],
    },
    account_uuids: {
      type: Array | String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      selectedList: [],
      plans: [],
      plansMeta: [],
      page: 1,
      loadingMore: 'Loading more...',
      loading: false,
      board: {
        lists: [],
      },
    };
  },

  created() {
    this.grabPlans();
  },

  mounted() {
    this.createIntersectionObserver();
  },

  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {
    async grabPlans() {
      this.loading = true;

      if (!this.account_uuids) {
        this.loading = false;
        return;
      }

      const params = {
        per_page: 50,
        page: this.page,
        include: 'accounts',
      };
      if (Array.isArray(this.account_uuids)) {
        params['filter[account_uuids]'] = this.account_uuids.map((account) => account).join(',');
      } else {
        params['filter[account_uuids]'] = this.account_uuids;
      }

      try {
        const rsp = await plansAPI.all({ params });
        let data = rsp.data.data;

        if (this.exclude.length) {
          data = data.filter((item) => !this.exclude.includes(item.type));
        }

        if (this.currentPlanUuid) {
          data = data.filter((item) => item.uuid !== this.currentPlanUuid);
        }

        this.plans = data;
        this.plansMeta = rsp.data.meta;
        this.setSelectedValue(this.value);
      } catch (err) {
        this.$nuxt.$emit(
          'notification',
          'There was an issue getting the plans for this circle, please try again',
          'warning',
          true
        );
      } finally {
        this.loading = false;
      }
    },

    setSelectedValue(value) {
      let selected = null;

      if (Array.isArray(value)) {
        selected = value.map((item) => this.plans.find((plan) => item === plan.uuid)).filter(Boolean);
      } else if (value && typeof value === 'object' && value.uuid) {
        selected = this.plans.find((plan) => plan.uuid === value.uuid) || value;
      } else if (value) {
        selected = this.plans.find((item) => value === item.uuid);
      }

      this.selectedList = selected;
    },

    createIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5,
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.fetchNextPage();
          }
        });
      }, options);

      this.observer.observe(this.$refs.loadMoreTrigger);
    },

    async fetchNextPage() {
      if (this.page >= this.plansMeta.last_page) {
        this.loadingMore = '';
        return;
      }

      this.page++;
      this.loading = true;

      try {
        const rsp = await plansAPI.all({
          params: {
            per_page: 25,
            page: this.page,
            include: 'accounts',
          },
        });
        this.plans = [...this.plans, ...rsp.data.data];
        this.plansMeta = rsp.data.meta;
      } catch (err) {
        this.$nuxt.$emit('notification', 'There was an issue getting more plans, please try again', 'warning', true);
      } finally {
        this.loading = false;
        if (this.page === this.plansMeta.last_page) {
          this.loadingMore = '';
        }
      }
    },
  },

  watch: {
    account_uuids() {
      this.grabPlans();
    },
    selectedList(new_val) {
      if (this.preserveModelType) {
        this.$emit('input', new_val);
      } else if (new_val === null) {
        this.$emit('input', null);
      } else if (!Array.isArray(new_val)) {
        if (new_val && new_val.uuid !== null) {
          this.$emit('input', new_val.uuid);
        }
      } else {
        let reduced = new_val.filter((item) => item && item.uuid !== null).map((item) => item.uuid);
        this.$emit('input', reduced);
      }
    },

    value: {
      async handler(new_val, old_val) {
        if (this.preserveModelType) {
          if (this.loading) {
            await this.grabPlans();
          }

          this.setSelectedValue(new_val);
        }
      },
    },
  },
};
